import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLang, RESERVE_URL } from "../context/LanguageContext";

const LOGO_FULL = "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/vpvrvwp5_logo%20imagotip%20xpro%20blanco.png";

export default function Navbar() {
  const { t, lang, setLang, bannerOpen } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "facilities", href: "#facilities" },
    { key: "services", href: "#services" },
    { key: "schedule", href: "#schedule" },
    { key: "pricing", href: "#pricing" },
    { key: "contact", href: "#location" },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 z-50 transition-all duration-300 top-0 ${scrolled ? "bg-black/95 backdrop-blur-xl border-b border-white/5" : "bg-black"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#top" data-testid="navbar-logo" className="flex items-center gap-2">
            <img src={LOGO_FULL} alt="XPRO Fitness" className="h-10 w-auto" />
          </a>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                className="text-[13px] tracking-[0.22em] uppercase font-semibold text-white/80 hover:text-[#FFCC00] transition-colors"
              >
                {t.nav[l.key]}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageToggle lang={lang} setLang={setLang} />
            <a
              href={RESERVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-button"
              className="hidden md:inline-flex items-center px-5 py-3 bg-[#FFCC00] text-black font-bold text-[12px] tracking-[0.22em] uppercase hover:bg-white transition-colors"
            >
              {t.nav.cta}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-white/15 text-white"
              aria-label="menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-black border-t border-white/5 ${
          open ? "max-h-[480px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.key}`}
              className="text-base tracking-[0.2em] uppercase font-semibold text-white/80 hover:text-[#FFCC00]"
            >
              {t.nav[l.key]}
            </a>
          ))}
          <a
            href={RESERVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-cta"
            className="inline-flex items-center justify-center px-5 py-4 bg-[#FFCC00] text-black font-bold text-[12px] tracking-[0.22em] uppercase mt-2"
          >
            {t.nav.cta}
          </a>
        </div>
      </div>
    </header>
  );
}

function LanguageToggle({ lang, setLang }) {
  return (
    <div
      data-testid="language-toggle"
      className="inline-flex items-center border border-white/15 text-[12px] tracking-widest font-semibold select-none"
    >
      <button
        data-testid="lang-ca-btn"
        onClick={() => setLang("ca")}
        className={`px-3 py-2 transition-colors ${
          lang === "ca" ? "bg-[#FFCC00] text-black" : "text-white/70 hover:text-white"
        }`}
      >
        CA
      </button>
      <button
        data-testid="lang-es-btn"
        onClick={() => setLang("es")}
        className={`px-3 py-2 transition-colors ${
          lang === "es" ? "bg-[#FFCC00] text-black" : "text-white/70 hover:text-white"
        }`}
      >
        ES
      </button>
    </div>
  );
}
